import React, { CSSProperties, useMemo } from "react";
import { Cell, flexRender } from "@tanstack/react-table";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconButton } from "../../../controls";

export interface TbodyCellProps {
  cell: Cell<unknown, unknown>;
  dataTestId?: string;
  rowHeight?: number | string;
  enableTableViewEdit?: boolean;
  lastPinnedColumn?: number;
}

const TbodyCell: React.FC<TbodyCellProps> = ({
  cell,
  dataTestId,
  rowHeight,
  enableTableViewEdit,
  lastPinnedColumn,
}) => {
  const { row } = cell;
  const cellValue = cell.getContext().getValue();
  const dataAttribute = {
    [`data-${cell.column.id}`]: cellValue,
  };

  const { setNodeRef, transform, isDragging } = useSortable({
    id: cell.column.id,
  });

  const styles = useMemo<CSSProperties>(() => {
    const { column } = cell;
    // hideOnEdit is a custom columnDef meta property not present on the base ColumnDef type
    const hidePin = (column?.columnDef as { hideOnEdit?: boolean })?.hideOnEdit;
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      !hidePin && !!isPinned && column.getPinnedIndex() === lastPinnedColumn;
    return {
      boxShadow: isLastLeftPinnedColumn
        ? "inset -1px 0px 0px 0px var(--border-color)"
        : "",
      position: isPinned ? "sticky" : "relative",
      left: isPinned ? `${column.getStart("left")}px` : "unset",
      zIndex: isPinned || isDragging ? 2 : 1,
      backgroundColor:
        isPinned || isDragging
          ? "inherit"
          : cell.getIsPlaceholder()
            ? "var(--table-placeholder-background-color)"
            : "unset",
    };
  }, [cell, lastPinnedColumn, isDragging, cell?.column?.getStart("left")]);

  return (
    <td
      className={`td`}
      key={cell.id}
      {...{
        style: {
          width: cell?.column?.getSize(),
          height: rowHeight,
          transform: CSS.Translate.toString(transform),
          ...styles,
        },
      }}
      {...dataAttribute}
      data-testid={`${dataTestId}-cell-${cell.row.index}_${cell.column.id}`}
      ref={enableTableViewEdit ? setNodeRef : null}
    >
      <div className={`td-content-wrapper`}>
        {cell?.getIsGrouped() ? (
          <div className="grouped-cell">
            <IconButton
              size="small"
              className={row?.getIsExpanded() ? "is-expanded" : "is-collapsed"}
              isIcon={"seem-icon-right-arrow"}
            />
            <div className="grouped-cell-content">
              {flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}
            </div>
            <div className={"sub-rows-count"}>({row?.subRows?.length})</div>
          </div>
        ) : (
          !cell?.getIsPlaceholder() &&
          flexRender(cell?.column?.columnDef?.cell, cell?.getContext())
        )}
      </div>
    </td>
  );
};

export default TbodyCell;
